import { NavLink, useNavigate } from 'react-router-dom';
import type { DashboardData } from '@/types';
import { Icon } from '@/components/Icon';
import { Sparkline } from '@/components/Sparkline';
import { formatCurrency } from '@/lib/money';
import { dniLabel } from './dniLabel';

/**
 * Prezentacyjne widgety dashboardu. Każdy czyta wycinek tego samego `DashboardData`
 * (jedno zapytanie `/api/dashboard`). JSX przeniesiony 1:1 z dawnego monolitu Dashboard.tsx —
 * rejestr (registry.tsx) decyduje o kolejności/szerokości/widoczności.
 */
export interface WidgetProps {
  data: DashboardData;
}

// ── KPI ──────────────────────────────────────────────────────────────────

export function KpiActiveIntakes({ data }: WidgetProps) {
  const navigate = useNavigate();
  return (
    <div className="kpi cursor-pointer hover:bg-ink-50/50 hover:shadow-sm transition group h-full"
         onClick={() => navigate('/intakes?filter=ACTIVE')}
         title="Pokaż wszystkie aktywne ujęcia">
      <div className="absolute inset-y-0 left-0 w-[3px] bg-water-500"/>
      <Icon name="chevron" className="absolute top-3.5 right-3 w-3 h-3 text-ink-300 group-hover:text-water-500 transition"/>
      <div className="kpi-label">Aktywne ujęcia</div>
      <div className="kpi-value">{data.activeIntakes}</div>
      <div className="flex gap-3.5 mt-2.5 text-[12px]">
        <NavLink to="/intakes?filter=ACTIVE&authority=DISTRICT"
                 onClick={e => e.stopPropagation()}
                 className="no-underline text-inherit hover:text-water-600">
          <div className="text-ink-900 text-[16px] font-semibold">{data.districtIntakes}</div>
          <div className="text-ink-400 text-[10px] uppercase tracking-wider">Starosta</div>
        </NavLink>
        <NavLink to="/intakes?filter=ACTIVE&authority=REGIONAL"
                 onClick={e => e.stopPropagation()}
                 className="no-underline text-inherit hover:text-water-600">
          <div className="text-ink-900 text-[16px] font-semibold">{data.regionalIntakes}</div>
          <div className="text-ink-400 text-[10px] uppercase tracking-wider">Marszałek</div>
        </NavLink>
      </div>
    </div>
  );
}

export function KpiStudies({ data }: WidgetProps) {
  const navigate = useNavigate();
  return (
    <div className="kpi cursor-pointer hover:bg-ink-50/50 hover:shadow-sm transition group h-full"
         onClick={() => navigate('/studies?filter=aktywne')}
         title="Pokaż wszystkie aktywne opracowania">
      <div className="absolute inset-y-0 left-0 w-[3px] bg-water-500"/>
      <Icon name="chevron" className="absolute top-3.5 right-3 w-3 h-3 text-ink-300 group-hover:text-water-500 transition"/>
      <div className="kpi-label">Opracowania w toku</div>
      <div className="kpi-value">{data.studiesInProgress}</div>
      <div className="flex gap-3.5 mt-2.5 text-[12px]">
        <div><div className="text-ink-900 text-[16px] font-semibold">{data.officeWork}</div><div className="text-ink-400 text-[10px] uppercase tracking-wider">Kameralka</div></div>
        <div><div className="text-ink-900 text-[16px] font-semibold">{data.toBeSigned}</div><div className="text-ink-400 text-[10px] uppercase tracking-wider">Podpis</div></div>
      </div>
    </div>
  );
}

export function KpiDeadlines({ data }: WidgetProps) {
  const navigate = useNavigate();
  return (
    <div className="kpi cursor-pointer hover:bg-ink-50/50 hover:shadow-sm transition group h-full"
         onClick={() => navigate('/studies?filter=aktywne')}
         title="Lista opracowań posortowana po deadline rosnąco">
      <div className="absolute inset-y-0 left-0 w-[3px] bg-amber-500"/>
      <Icon name="chevron" className="absolute top-3.5 right-3 w-3 h-3 text-ink-300 group-hover:text-amber-500 transition"/>
      {data.criticalDeadlines > 0 && <div className="absolute top-3.5 right-9 text-[11px] font-semibold text-red-500">{data.criticalDeadlines} krytyczne</div>}
      <div className="kpi-label">Terminy w tym tyg.</div>
      <div className="kpi-value">{data.deadlinesThisWeek.length}</div>
    </div>
  );
}

export function KpiAiDrafts({ data }: WidgetProps) {
  const navigate = useNavigate();
  return (
    <div className="kpi cursor-pointer hover:bg-ink-50/50 hover:shadow-sm transition group h-full"
         onClick={() => navigate('/ai-editor')}
         title="Otwórz edytor AI">
      <div className="absolute inset-y-0 left-0 w-[3px] bg-ai-500"/>
      <Icon name="chevron" className="absolute top-3.5 right-3 w-3 h-3 text-ink-300 group-hover:text-ai-600 transition"/>
      <div className="absolute top-3.5 right-9 text-[11px] font-semibold text-ai-600">AI · aktywne</div>
      <div className="kpi-label">Drafty AI (7 dni)</div>
      <div className="kpi-value">{data.draftsThisWeek}</div>
      <div className="flex gap-3.5 mt-2.5 text-[12px]">
        <div><div className="text-ink-900 text-[16px] font-semibold">{data.draftsAccepted}</div><div className="text-ink-400 text-[10px] uppercase tracking-wider">Zaakceptowane</div></div>
      </div>
    </div>
  );
}

// ── Karty ────────────────────────────────────────────────────────────────

export function TypesCountWidget({ data }: WidgetProps) {
  return (
    <div className="card h-full">
      <div className="card-head">
        <div>
          <div className="card-title">Typy opracowań w toku</div>
          <div className="text-ink-400 text-[12px]">Rozkład aktywnych projektów wg 8 typów</div>
        </div>
        <NavLink to="/studies" className="btn btn-ghost btn-sm ml-auto">Wszystkie →</NavLink>
      </div>
      <div className="card-body">
        {Object.entries(data.typesCount).length === 0
          ? <div className="text-ink-400">Brak aktywnych opracowań.</div>
          : (
            <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-[12px]">
              {Object.entries(data.typesCount).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-ink-700">
                    <span className="w-2.5 h-2.5 rounded-sm bg-water-500"/>
                    {k}
                  </div>
                  <span className="font-mono tabular-nums text-ink-500 text-[12px]">{v}</span>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

export function TopIntakesWidget({ data }: WidgetProps) {
  return (
    <div className="card h-full">
      <div className="card-head"><div className="card-title">Najwydajniejsze ujęcia</div>
        <NavLink to="/intakes" className="btn btn-ghost btn-sm ml-auto">Wszystkie ujęcia →</NavLink></div>
      <div className="card-body !px-[18px] !py-1.5">
        {data.topIntakes.map(u => (
          <NavLink key={u.id} to={`/intakes/${u.id}`} className="flex items-center gap-3 py-3 border-b border-ink-100 last:border-b-0 no-underline text-inherit hover:bg-ink-50 -mx-[18px] px-[18px]">
            <div className="w-9 h-9 rounded-lg bg-water-100 text-water-600 grid place-items-center shrink-0">
              <Icon name="droplet"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[13px] text-ink-900">
                {u.name}
                <span className={'pill ml-1.5 ' + (u.authority === 'REGIONAL' ? 'pill-marsz' : 'pill-star')}>
                  <span className="pill-dot"/>{u.authorityName}
                </span>
              </div>
              {/* clientName jest puste dla użytkownika bez CLIENT_VIEW — wtedy sam wydatek. */}
              <div className="text-[12px] text-ink-400">
                {u.clientName ? `${u.clientName} · ` : ''}Q={u.qM3h ?? '—'} m³/h
              </div>
            </div>
            <span className="font-mono text-[11px] bg-ink-50 px-2 py-[3px] rounded text-ink-600">{u.boreholeCount} otworów</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export function MyTasksWidget({ data }: WidgetProps) {
  return (
    <div className="card h-full">
      <div className="card-head"><div className="card-title">Moje zadania na dziś</div></div>
      <div className="card-body !px-[18px] !py-1.5">
        {data.myTasks.length === 0 && <div className="text-ink-400 py-3">Brak otwartych zadań.</div>}
        {data.myTasks.map(z => (
          <div key={z.id} className="flex items-start gap-2.5 py-2.5 border-b border-ink-100 last:border-b-0">
            <div className="pt-1">
              <span className={'sdot ' + (z.priority === 'CRITICAL' ? 'sdot-red' : z.priority === 'HIGH' ? 'sdot-amber' : 'sdot-blue')}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{z.title}</div>
              <div className="text-[11px] text-ink-400">
                {z.studyNumber && <>{z.studyNumber} · </>}
                {z.deadline && <>termin: {z.deadline}</>}
              </div>
            </div>
            {z.studyId && (
              <NavLink to={`/studies/${z.studyId}`} className="btn btn-secondary btn-sm">Otwórz</NavLink>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function DeadlinesAlertsWidget({ data }: WidgetProps) {
  return (
    <div className="card h-full">
      <div className="card-head">
        <div className="card-title">Terminy i alarmy</div>
        {data.criticalDeadlines > 0 && <span className="pill pill-red ml-auto">{data.criticalDeadlines} krytyczne</span>}
      </div>
      <div className="card-body !p-3">
        {data.deadlinesThisWeek.length === 0 && <div className="text-ink-400 text-[13px]">Brak pilnych terminów.</div>}
        {data.deadlinesThisWeek.map(o => {
          const days = o.deadline ? Math.ceil((new Date(o.deadline).getTime() - Date.now()) / (1000 * 3600 * 24)) : 0;
          const kolor = days <= 1 ? 'border-l-red-500 bg-gradient-to-r from-red-100 to-white/30' : 'border-l-amber-500 bg-gradient-to-r from-amber-100 to-white/30';
          return (
            <NavLink key={o.id} to={`/studies/${o.id}`} className={`flex items-center gap-2.5 p-2.5 rounded-md border border-ink-100 border-l-[3px] mb-2 no-underline text-inherit ${kolor}`}>
              <div className={'font-mono text-[11px] font-semibold whitespace-nowrap ' + (days <= 1 ? 'text-red-500' : 'text-amber-500')}>
                {dniLabel(days)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-ink-800 truncate">{o.title}</div>
                <div className="text-[11px] text-ink-400">{o.number} · {o.authorityName}</div>
              </div>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}

export function ComplianceWidget({ data }: WidgetProps) {
  return (
    <div className="card h-full">
      <div className="card-head">
        <div className="card-title">Terminy: przeglądy, BHP, ubezpieczenia</div>
        {data.complianceOverdue > 0 && <span className="pill pill-red ml-auto">{data.complianceOverdue} po terminie</span>}
      </div>
      <div className="card-body !p-3">
        {data.complianceDeadlines.length === 0 && <div className="text-ink-400 text-[13px]">Brak nadchodzących terminów.</div>}
        {data.complianceDeadlines.map(c => {
          const d = c.daysUntil ?? 0;
          const kolor = c.urgency === 'OVERDUE' ? 'border-l-red-500 bg-gradient-to-r from-red-100 to-white/30'
            : 'border-l-amber-500 bg-gradient-to-r from-amber-100 to-white/30';
          const label = dniLabel(d);
          return (
            <NavLink key={c.id} to="/terminy" className={`flex items-center gap-2.5 p-2.5 rounded-md border border-ink-100 border-l-[3px] mb-2 no-underline text-inherit ${kolor}`}>
              <div className={'font-mono text-[11px] font-semibold whitespace-nowrap ' + (c.urgency === 'OVERDUE' ? 'text-red-500' : 'text-amber-500')}>
                {label}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-ink-800 truncate">{c.title}</div>
                <div className="text-[11px] text-ink-400">{c.categoryName}{c.companyName ? ' · ' + c.companyName : ''}{c.userName ? ' · ' + c.userName : ''}</div>
              </div>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Nadchodzące i zaległe płatności cykliczne (V153). Osobno sygnalizuje brak faktury — to
 * odrębny problem od braku zapłaty i wymaga innego działania (pobrać dokument, nie przelać).
 */
export function SubscriptionPaymentsWidget({ data }: WidgetProps) {
  const rows = data.subscriptionPayments ?? [];
  return (
    <div className="card h-full">
      <div className="card-head">
        <div className="card-title">Nadchodzące płatności</div>
        <div className="ml-auto flex gap-1">
          {data.subscriptionMissingInvoices > 0 && (
            <span className="pill pill-amber" title="Okresy po terminie bez faktury">
              {data.subscriptionMissingInvoices} bez faktury
            </span>
          )}
          {data.subscriptionOverdue > 0 && <span className="pill pill-red">{data.subscriptionOverdue} po terminie</span>}
        </div>
      </div>
      <div className="card-body !p-3">
        {rows.length === 0 && <div className="text-ink-400 text-[13px]">Brak płatności w najbliższych 30 dniach.</div>}
        {rows.map(p => {
          const kolor = p.urgency === 'OVERDUE'
            ? 'border-l-red-500 bg-gradient-to-r from-red-100 to-white/30'
            : 'border-l-amber-500 bg-gradient-to-r from-amber-100 to-white/30';
          const kwota = p.plannedAmount != null ? formatCurrency(p.plannedAmount, p.currency) : null;
          return (
            <NavLink key={p.id} to={`/subskrypcje/${p.subscriptionId}`}
                     className={`flex items-center gap-2.5 p-2.5 rounded-md border border-ink-100 border-l-[3px] mb-2 no-underline text-inherit ${kolor}`}>
              <div className={'font-mono text-[11px] font-semibold whitespace-nowrap ' + (p.urgency === 'OVERDUE' ? 'text-red-500' : 'text-amber-500')}>
                {dniLabel(p.daysUntil)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-ink-800 truncate">{p.subscriptionName}</div>
                <div className="text-[11px] text-ink-400">
                  {p.dueDate}{kwota ? ' · ' + kwota : ''}
                  {p.invoiceMissing && <span className="text-amber-600 font-semibold"> · brak faktury</span>}
                </div>
              </div>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}

export function HrFleetWidget({ data }: WidgetProps) {
  const hrFleet = [...(data.hrDeadlines ?? []), ...(data.fleetDeadlines ?? [])]
    .sort((a, b) => a.daysUntil - b.daysUntil);
  const overdue = (data.hrOverdue ?? 0) + (data.fleetOverdue ?? 0);
  return (
    <div className="card h-full">
      <div className="card-head">
        <div className="card-title">Terminy: Kadry i Pojazdy</div>
        {overdue > 0 && <span className="pill pill-red ml-auto">{overdue} po terminie</span>}
      </div>
      <div className="card-body !p-3">
        {hrFleet.length === 0 && <div className="text-ink-400 text-[13px]">Brak nadchodzących terminów.</div>}
        {hrFleet.map((r, i) => {
          const kolor = r.urgency === 'OVERDUE' ? 'border-l-red-500 bg-gradient-to-r from-red-100 to-white/30'
            : 'border-l-amber-500 bg-gradient-to-r from-amber-100 to-white/30';
          return (
            <NavLink key={r.source + i} to={r.link} className={`flex items-center gap-2.5 p-2.5 rounded-md border border-ink-100 border-l-[3px] mb-2 no-underline text-inherit ${kolor}`}>
              <div className={'font-mono text-[11px] font-semibold whitespace-nowrap ' + (r.urgency === 'OVERDUE' ? 'text-red-500' : 'text-amber-500')}>
                {dniLabel(r.daysUntil)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-ink-800 truncate">{r.label}</div>
                <div className="text-[11px] text-ink-400">{r.sourceName} · {r.sublabel}</div>
              </div>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}

export function UpcomingTripsWidget({ data }: WidgetProps) {
  return (
    <div className="card h-full">
      <div className="card-head">
        <div className="card-title">Najbliższe wyjazdy brygad</div>
        <NavLink to="/kalendarz" className="btn btn-ghost btn-sm ml-auto">Kalendarz →</NavLink>
      </div>
      <div className="card-body !p-3">
        {(data.upcomingTrips ?? []).length === 0 && <div className="text-ink-400 text-[13px]">Brak zaplanowanych wyjazdów.</div>}
        {(data.upcomingTrips ?? []).map(t => (
          <NavLink key={t.id} to="/kalendarz" className="flex items-center gap-2.5 p-2.5 rounded-md border border-ink-100 mb-2 no-underline text-inherit hover:bg-ink-50">
            <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: t.color || '#6b7280' }}/>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-ink-800 truncate">{t.title}</div>
              <div className="text-[11px] text-ink-400">
                {t.startDate}{t.endDate !== t.startDate ? ' – ' + t.endDate : ''}
                {t.brigadeName ? ' · ' + t.brigadeName : ''}
                {t.crewCount > 0 ? ` · ${t.crewCount} os.` : ''}
              </div>
            </div>
            {t.statusName && <span className="text-[10px] text-ink-400 whitespace-nowrap">{t.statusName}</span>}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export function AiAssistantWidget({ data }: WidgetProps) {
  return (
    <div className="card h-full">
      <div className="card-head">
        <div className="flex items-center gap-2">
          <div className="sdot sdot-ai sdot-live"/>
          <div className="card-title">Asystent AI</div>
        </div>
      </div>
      <div className="card-body">
        <Sparkline
          labels={data.aiDraftsByDay.map(d => d.dayLabel)}
          data={data.aiDraftsByDay.map(d => d.count)}
          color="#7c5cff"
          height={40}
        />
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div><div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">Drafty tyg.</div><div className="text-[18px] font-semibold font-mono">{data.draftsThisWeek}</div></div>
          <div><div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">Zaakceptowane</div><div className="text-[18px] font-semibold text-green-500 font-mono">{data.draftsAccepted}</div></div>
        </div>
        <hr className="border-0 border-t border-ink-100 my-3.5"/>
        <NavLink to="/ai-editor" className="btn btn-ai btn-sm w-full justify-center">Otwórz edytor AI →</NavLink>
      </div>
    </div>
  );
}

export function RagKbWidget({ data }: WidgetProps) {
  return (
    <div className="card h-full">
      <div className="card-head"><div className="card-title">Baza wiedzy (RAG)</div>
        <span className="pill pill-green ml-auto"><span className="pill-dot"/> Zindeksowana</span></div>
      <div className="card-body">
        <div className="flex gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">Documentów</div>
            <div className="font-mono text-[22px] font-semibold">{data.archiveCount}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">Indeks</div>
            <div className="font-mono text-[22px] font-semibold">
              {data.driveStats ? `${Math.round(data.driveStats.percent)}%` : '—'}
            </div>
          </div>
        </div>
        <hr className="border-0 border-t border-ink-100 my-3.5"/>
        <NavLink to="/drive" className="flex items-center gap-2 text-[12px] text-ink-500 no-underline hover:text-ink-800">
          <Icon name="cloud"/>
          Google Drive · {data.driveStats ? `${data.driveStats.indexedFiles} / ${data.driveStats.totalFiles}` : '—'}
        </NavLink>
      </div>
    </div>
  );
}
